/**
 * IfTriggerExtension — surfaces the Logic Wizard on demand, never automatically.
 *
 * Behaviour:
 *  - When the cursor is in a paragraph whose text matches /^\s*IF\b/ (case-
 *    insensitive), a small "Continue in Logic Wizard" chip is rendered as a
 *    ProseMirror decoration widget at the end of that line. Clicking it
 *    dispatches `dataq:open-logic-wizard`.
 *  - The chip disappears when the cursor moves to a different paragraph.
 *  - Typing `IF ` does NOT open the wizard automatically — users keep their
 *    flow; the chip is a passive offer they can ignore.
 *  - The chip is the only entry point. There is no global keyboard shortcut
 *    so we don't surprise users on non-IF lines or shadow browser bindings
 *    like Ctrl+L.
 *
 * The dispatch payload covers BOTH the IF line and any "simple body" that
 * follows it (TERMINATE / SKIP TO / BEGIN-END). If the next paragraph is
 * something else (e.g. a Q definition), the captured range stays just the
 * IF paragraph so the wizard can't accidentally overwrite a question below.
 */
import { Extension } from "@tiptap/core"
import { Plugin, PluginKey } from "@tiptap/pm/state"
import { Decoration, DecorationSet } from "@tiptap/pm/view"
import type { EditorView } from "@tiptap/pm/view"
import type { Node as ProseMirrorNode } from "@tiptap/pm/model"

export interface IfTriggerEventDetail {
  /** "create" — line is empty after IF; "edit" — existing condition to parse. */
  mode: "create" | "edit"
  /**
   * Full text of the captured block. Includes the IF line and any captured
   * body lines, joined by `\n`.
   */
  lineText: string
  /**
   * Paragraph-node-bound ProseMirror positions for replacement. Inclusive of
   * the IF paragraph and any extra paragraphs the body scan consumed.
   */
  from: number
  to: number
  /** 1-based paragraph index of the IF line — informational only. */
  paragraphIndex: number
}

const IF_LINE_RE = /^\s*IF\b\s*(.*)$/i
const TERMINATE_RE = /^\s*TERMINATE\s*$/i
const SKIP_TO_RE = /^\s*SKIP\s+TO\b/i
const BEGIN_RE = /^\s*BEGIN\s*$/i
const END_RE = /^\s*END\s*$/i

function dispatchOpen(detail: IfTriggerEventDetail) {
  window.dispatchEvent(
    new CustomEvent<IfTriggerEventDetail>("dataq:open-logic-wizard", { detail }),
  )
}

interface ParagraphHit {
  text: string
  /** Inside-paragraph text bounds. */
  from: number
  to: number
  /** Paragraph node bounds. */
  paragraphFrom: number
  paragraphTo: number
  /** 1-based paragraph index in the document. */
  index: number
}

/**
 * Walk the doc to enumerate all paragraphs as { text, paragraphFrom, paragraphTo }.
 */
function listParagraphs(doc: ProseMirrorNode): ParagraphHit[] {
  const out: ParagraphHit[] = []
  let idx = 0
  doc.descendants((node, nodePos) => {
    if (node.type.name !== "paragraph") return true
    idx += 1
    out.push({
      text: node.textContent,
      from: nodePos + 1,
      to: nodePos + node.nodeSize - 1,
      paragraphFrom: nodePos,
      paragraphTo: nodePos + node.nodeSize,
      index: idx,
    })
    return false
  })
  return out
}

function findParagraphIndexAt(paragraphs: ParagraphHit[], pos: number): number {
  for (let i = 0; i < paragraphs.length; i++) {
    const p = paragraphs[i]
    if (pos >= p.paragraphFrom && pos <= p.paragraphTo) return i
  }
  return -1
}

/**
 * Starting at `start`, scan forward to capture any "simple body" paragraphs
 * (TERMINATE / SKIP TO / BEGIN-END). Returns the index of the last paragraph
 * to include in the replacement range, or `start` itself if nothing extra is
 * captured.
 *
 * We deliberately don't capture `Q LABEL: …` blocks: questions can span many
 * lines (with options, decorators, etc.) and we don't want to risk
 * overwriting them when the user only meant to edit the IF.
 */
function scanBodyForward(paragraphs: ParagraphHit[], start: number): number {
  // Skip blank paragraphs immediately after the IF.
  let i = start + 1
  while (i < paragraphs.length && paragraphs[i].text.trim() === "") {
    i++
  }
  if (i >= paragraphs.length) return start
  const first = paragraphs[i].text

  if (TERMINATE_RE.test(first) || SKIP_TO_RE.test(first)) {
    return i
  }
  if (BEGIN_RE.test(first)) {
    let depth = 1
    let j = i + 1
    while (j < paragraphs.length) {
      const t = paragraphs[j].text
      if (BEGIN_RE.test(t)) depth++
      else if (END_RE.test(t)) {
        depth--
        if (depth === 0) return j
      }
      j++
    }
    // Unmatched BEGIN — don't extend the range.
    return start
  }
  return start
}

/** Build a click handler that re-resolves the IF block at click time. */
function makeChipClickHandler(getView: () => EditorView | null) {
  return (ev: Event) => {
    ev.preventDefault()
    ev.stopPropagation()
    const view = getView()
    if (!view) return
    const paragraphs = listParagraphs(view.state.doc)
    const idx = findParagraphIndexAt(paragraphs, view.state.selection.from)
    if (idx < 0) return
    const ifPara = paragraphs[idx]
    const m = ifPara.text.match(IF_LINE_RE)
    if (!m) return
    const lastIdx = scanBodyForward(paragraphs, idx)
    const captured = paragraphs.slice(idx, lastIdx + 1)
    const text = captured.map((p) => p.text).join("\n")
    const condition = m[1].trim()
    dispatchOpen({
      mode: condition ? "edit" : "create",
      lineText: text,
      from: ifPara.paragraphFrom,
      to: paragraphs[lastIdx].paragraphTo,
      paragraphIndex: ifPara.index,
    })
  }
}

/**
 * Render the floating chip as a DOM node. Tailwind-equivalent inline styles
 * to match the project's brand cyan and chip idiom.
 */
function renderChip(
  getView: () => EditorView | null,
): HTMLElement {
  const span = document.createElement("span")
  span.className = "dataq-if-trigger-chip"
  span.setAttribute("contenteditable", "false")
  span.style.cssText = [
    "display: inline-flex",
    "align-items: center",
    "gap: 4px",
    "margin-left: 8px",
    "padding: 0 10px",
    "height: 18px",
    "line-height: 1",
    "border-radius: 9999px",
    "background-color: #36C3ED",
    "color: white",
    "font-size: 11px",
    "font-weight: 500",
    "cursor: pointer",
    "user-select: none",
    // CSS `vertical-align: middle` lands at parent_baseline - x-height/2,
    // which is slightly above the visual centre of where the lowercase text
    // actually appears. A small downward translate corrects to the eye.
    "vertical-align: middle",
    "transform: translateY(-3px)",
    "box-shadow: 0 1px 2px rgba(0,0,0,0.1)",
    "white-space: nowrap",
  ].join("; ")
  span.title = "Open Logic Wizard"
  const icon = document.createElementNS("http://www.w3.org/2000/svg", "svg")
  icon.setAttribute("width", "12")
  icon.setAttribute("height", "12")
  icon.setAttribute("viewBox", "0 0 24 24")
  icon.setAttribute("fill", "none")
  icon.setAttribute("stroke", "currentColor")
  icon.setAttribute("stroke-width", "2")
  icon.setAttribute("stroke-linecap", "round")
  icon.setAttribute("stroke-linejoin", "round")
  icon.innerHTML =
    '<path d="m3 21 9-9"/><path d="M14.5 5.5 19 10"/><path d="m13 7 6-6 5 5-6 6"/><path d="m18 14 4 4"/>'
  span.appendChild(icon)
  const label = document.createElement("span")
  label.textContent = "Continue in Logic Wizard"
  span.appendChild(label)
  span.addEventListener("mousedown", makeChipClickHandler(getView))
  return span
}

const triggerKey = new PluginKey<DecorationSet>("dataq-if-trigger")

function buildDecorations(
  doc: ProseMirrorNode,
  selectionFrom: number,
  getView: () => EditorView | null,
): DecorationSet {
  const paragraphs = listParagraphs(doc)
  const idx = findParagraphIndexAt(paragraphs, selectionFrom)
  if (idx < 0) return DecorationSet.empty
  const para = paragraphs[idx]
  if (!IF_LINE_RE.test(para.text)) return DecorationSet.empty
  const widget = Decoration.widget(para.to, () => renderChip(getView), {
    side: 1,
    ignoreSelection: true,
    key: "if-trigger-chip",
  })
  return DecorationSet.create(doc, [widget])
}

export const IfTriggerExtension = Extension.create({
  name: "ifTrigger",

  addProseMirrorPlugins() {
    let viewRef: EditorView | null = null
    const getView = () => viewRef
    return [
      new Plugin<DecorationSet>({
        key: triggerKey,
        view: (editorView) => {
          viewRef = editorView
          return {
            destroy() {
              viewRef = null
            },
          }
        },
        state: {
          init: (_, instance) =>
            buildDecorations(instance.doc, instance.selection.from, getView),
          apply: (tr, old) => {
            if (!tr.docChanged && !tr.selectionSet) {
              return old.map(tr.mapping, tr.doc)
            }
            return buildDecorations(tr.doc, tr.selection.from, getView)
          },
        },
        props: {
          decorations(state) {
            return triggerKey.getState(state) ?? DecorationSet.empty
          },
        },
      }),
    ]
  },
})
